'use client';

import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { ValuePillar, getStrapiMedia } from '@/lib/strapi';
import Link from 'next/link';

interface InfoBlockProps {
  data: ValuePillar;
  mode: 'summary' | 'full';
  index?: number;
  lang?: string;
  isEven?: boolean;
  basePath?: string;
}

const UI_TEXT = {
  ru: { learnMore: "Подробнее" },
  ro: { learnMore: "Mai mult" },
  en: { learnMore: "Learn more" },
  uk: { learnMore: "Докладніше" },
} as const;

/**
 * Рендеринг блоков Strapi (Rich Text)
 */
function BlocksRenderer({ content }: { content: any[] }) {
  if (!content) return null;
  
  return (
    <div className="space-y-6">
      {content.map((block, idx) => {
        if (block.type === 'paragraph') {
          return (
            <p key={idx} className="text-xl text-slate-700 leading-relaxed font-light">
              {block.children.map((child: any, cidx: number) => (
                <span key={cidx} style={{ 
                  fontWeight: child.bold ? 'bold' : 'normal',
                  fontStyle: child.italic ? 'italic' : 'normal',
                  textDecoration: child.underline ? 'underline' : 'none'
                }}>
                  {child.text}
                </span>
              ))}
            </p>
          );
        }
        if (block.type === 'heading') {
          const level = block.level as 1 | 2 | 3 | 4 | 5 | 6;
          const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
          const fontClass = level === 1 ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl';
          return (
            <Tag key={idx} className={`${fontClass} font-black text-slate-900 mt-8 mb-4 tracking-tight`}>
              {block.children[0].text}
            </Tag>
          );
        }
        return null;
      })}
    </div>
  );
}

const itemVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 20,
    },
  },
};

export default function InfoBlock({ data, mode, index = 0, lang = 'ru', isEven = true, basePath = 'about' }: InfoBlockProps) {
  const iconUrl = getStrapiMedia(data.Icon?.url);
  const t = UI_TEXT[lang as keyof typeof UI_TEXT] || UI_TEXT.ru;

  if (mode === 'summary') {
    return (
      <motion.div
        variants={itemVariants}
        whileHover={{ 
          y: -10,
          transition: { duration: 0.2 }
        }}
        className="h-full"
      >
        <Link 
          href={`/${lang}/${basePath}#pillar-${data.id}`}
          className="group relative flex flex-col bg-white rounded-[3rem] overflow-hidden transition-all duration-700 hover:shadow-2xl border border-slate-200 min-h-[500px] h-full"
        >
          <div className="relative h-[320px] w-full overflow-hidden bg-slate-100">
            {iconUrl ? (
              <Image
                src={iconUrl}
                alt={data.Title}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-110"
                sizes="(max-width: 768px) 100vw, 33vw"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
                    <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 0 012-2h.93a2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <circle cx="12" cy="13" r="3" />
                    </svg>
                </div>
                <span className="text-xs font-bold text-slate-400 tracking-widest uppercase">About Preview</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>

          {/* Контентная часть */}
          <div className="p-10 flex flex-col justify-between flex-grow">
            <div>
              <h3 className="text-3xl font-black text-slate-900 mb-4 group-hover:text-blue-600 transition-colors tracking-tight">
                {data.Title}
              </h3>
              <p className="text-xl text-slate-500 line-clamp-3 mb-6 font-light leading-relaxed">
                {data.HomeDescription}
              </p>
            </div>

            <div className="flex items-center text-blue-600 font-black uppercase tracking-widest text-sm">
              {t.learnMore}
              <svg className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  // Режим full (для страницы About) - по аналогии с services/page.tsx
  return (
    <section 
      id={`pillar-${data.id}`}
      className={`py-24 flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-16 items-center scroll-mt-32`}
    >
      {/* Текстовый контент */}
      <div className="w-full lg:w-1/2 space-y-8">
        <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
          {data.Title}
        </h2>
        
        <div className="prose prose-slate prose-lg max-w-none">
          <BlocksRenderer content={data.FullDescription} />
        </div>
      </div>

      {/* Изображение */}
      <div className="w-full lg:w-1/2">
        <div className="relative aspect-[4/3] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white font-[var(--font-plus-jakarta)]">
          {iconUrl ? (
            <Image
              src={iconUrl}
              alt={data.Title}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          ) : (
            <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400">
              No Image
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
